import { GeometryTableSkeleton } from "@/scott/ui/blocks/geometry-table/geometry-table.skeleton";
import {
  PRODUCT_LISTING_SKELETON_COUNT,
  ProductListingD2CSkeleton,
} from "@/scott/ui/blocks/product-listing-d2c/product-listing-d2c.skeleton";
import type {
  GeometryTablePageBlock,
  PageBlock,
  ProductListingPageBlock,
} from "@/types/page-block";
import type { GeometryDataBlockProps } from "./geometry-data-block";
import type { ProductListingBlockProps } from "./product-listing-block";

function ProductListingBlockSkeleton(
  props: Readonly<Omit<ProductListingBlockProps, "pageContext">>
) {
  const specificProductCount = props.specificProducts?.length ?? 0;
  // Mirrors buildProductListingClientProps: a hand-picked product list has no
  // filters, sorting or pagination.
  const isSpecificList = specificProductCount > 0;

  return (
    <ProductListingD2CSkeleton
      anchorId={props.anchorId}
      backgroundColor={props.backgroundColor ?? "whitesmoke"}
      count={
        isSpecificList
          ? Math.min(specificProductCount, PRODUCT_LISTING_SKELETON_COUNT)
          : undefined
      }
      paddingBottom={props.paddingBottom}
      paddingTop={props.paddingTop}
      paginationMode={isSpecificList ? "showAll" : props.paginationMode}
      showFilters={!isSpecificList && (props.showFilters ?? true)}
      showSort={!isSpecificList && (props.showSorting ?? true)}
      title={props.title}
      variant={props.variant}
    />
  );
}

function GeometryDataBlockSkeleton(props: Readonly<GeometryDataBlockProps>) {
  return (
    <GeometryTableSkeleton
      anchorId={props.anchorId}
      backgroundColor={props.backgroundColor}
      columnWidth={props.columnWidth}
      paddingBottom={props.paddingBottom}
      paddingTop={props.paddingTop}
      showUnitToggle={props.showUnitToggle}
    />
  );
}

/**
 * Loading placeholder shown while a data-fetching server block streams in.
 * Returns null for block types without a dedicated skeleton.
 */
export function ServerBlockSkeleton(props: Readonly<{ block: PageBlock }>) {
  switch (props.block.__typename) {
    case "GeometryTable":
      return (
        <GeometryDataBlockSkeleton
          {...(props.block as GeometryTablePageBlock)}
        />
      );
    case "ProductListing":
      return (
        <ProductListingBlockSkeleton
          {...(props.block as ProductListingPageBlock)}
        />
      );
    default:
      return null;
  }
}
